import React, {useCallback, useEffect, useState} from 'react';
import {
  IFarmInputInvoice,
  IFarmInputOrder,
  IFarmInputPurchaseOrder,
  IInputMessage,
  ISupplierUserProfile,
  ISupportingDocumentInput,
} from 'constants/interfaces';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Hidden, {HiddenProps} from '@material-ui/core/Hidden'; // MUI v4 import
import {Route} from 'react-router-dom';
import moment, {Moment} from 'moment';
import GetAppIcon from '@material-ui/icons/GetApp';
import LibraryAddIcon from '@material-ui/icons/LibraryAdd';
// import NoteAddIcon from '@material-ui/icons/NoteAdd';
import AssignmentIcon from '@material-ui/icons/Assignment';
import {Breakpoint} from '@material-ui/core/styles/createBreakpoints';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';

import {NavigationService} from 'services/index';
import {PermissionsService} from 'services/PermissionsService';
import {
  EPurchaseOrderSupportingDocs,
  ESupplierUserPermissions,
} from 'constants/index';
import {
  apiSavePurchaseOrder,
  apiDeleteInvoiceSupportingDocument,
  apiGetDepotOrder,
  apiGetInputProducts,
  apiGetInvoiceSupportingDocuments,
  apiGETORDERMESSAGES,
  apiGetPurchaseOrdersByInvoice,
  apiSaveInvoiceSupportingDocument,
  apiSENDREPLACEMENTORDERMESSAGE,
  apiSENDTEXTORDERMESSAGE,
} from 'constants/Api';
import {reduxAddPurchaseOrder, reduxSetProducts} from 'appredux/actions';
import {ProductState} from 'appredux/reducers/reducer-products';
import {useSocketEvents} from 'appsockets/SocketEventProvider';

import {FarmDetailCard} from 'screens/Dashboard/Invoices/FarmerDetailCard/FarmDetailCard';
import {InvoiceProducts} from 'screens/Dashboard/Invoices/InvoiceProducts/InvoiceProducts';
import {InvoiceDetailCard} from 'screens/Dashboard/Invoices/InvoiceDetailCard/InvoiceDetailCard';
import {InvoiceLogisticsCard} from 'screens/Dashboard/Invoices/InvoiceLogisticsCard/InvoiceLogisticsCard';
import {InvoiceChat} from 'screens/Dashboard/Invoices/InvoiceChat/InvoiceChat';
import {SupportingDocuments} from 'components/SupportingDocuments/SupportingDocuments';
import {CreditNoteCard} from 'screens/Dashboard/Invoices/CreditNote/CreditNoteCard';
import {InvoicePurchaseOrders} from '../InvoicePurchaseOrders/InvoicePurchaseOrders';

/**
 * A small wrapper around MUI v4 <Hidden> that properly types the `children`.
 * This helps avoid the "Property 'children' does not exist on type 'HiddenProps'" error.
 */
type MyHiddenProps = HiddenProps & {children?: React.ReactNode};
const MyHidden: React.FC<MyHiddenProps> = ({children, ...rest}) => {
  return <Hidden {...rest}>{children}</Hidden>;
};

export const InvoiceDetail = (props: {
  user: ISupplierUserProfile;
  invoiceId: number;
  productState: ProductState;
  width: Breakpoint;
  /** When provided (e.g. from OrderDetailPage), skip initial fetch and use this data */
  initialInvoice?: IFarmInputInvoice;
}) => {
  const {user, invoiceId, initialInvoice} = props;

  const [tabIndex, setTabIndex] = useState(0);
  const showTabs = props.width === 'xs' || props.width === 'sm';

  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [invoice, setInvoice] = useState<IFarmInputInvoice | undefined>(
    initialInvoice,
  );
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isRefreshingMessages, setIsRefreshingMessages] = useState(false);
  const [messages, setMessages] = useState<IInputMessage[]>([]);

  const [isLoadingAllProducts, setIsLoadingAllProducts] = useState(false);
  const [isLoadingPurchaseOrder, setIsLoadingPurchaseOrder] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<
    IFarmInputPurchaseOrder[]
  >([]);
  const [
    showCreatePurchaseOrderConfirmation,
    setShowCreatePurchaseOrderConfirmation,
  ] = useState<boolean>(false);

  const {newInvoiceMessage} = useSocketEvents();

  /**
   * Loads the invoice details
   */
  const loadInvoice = useCallback((invId: number) => {
    apiGetDepotOrder(invId)
      .then(setInvoice)
      .catch((error) => {
        alert('Error loading invoice. Please try again later');
        console.error(error);
      })
      .finally(() => setIsLoadingInvoice(false));
  }, []);

  useEffect(() => {
    if (initialInvoice != null) {
      setInvoice(initialInvoice);
      return;
    }
    setIsLoadingInvoice(true);
    loadInvoice(invoiceId);
  }, [invoiceId, initialInvoice, loadInvoice]);

  /**
   * Loads the chat messages
   */
  const loadMessages = useCallback((invId: number) => {
    setIsLoadingMessages(true);
    apiGETORDERMESSAGES(invId)
      .then(setMessages)
      .catch((error) => {
        alert('Error loading messages. Please try again later');
        console.error(error);
      })
      .finally(() => setIsLoadingMessages(false));
  }, []);

  const refreshMessages = useCallback((invId: number) => {
    setIsRefreshingMessages(true);
    apiGETORDERMESSAGES(invId)
      .then(setMessages)
      .catch((error) => {
        alert('Error loading messages. Please try again later');
        console.error(error);
      })
      .finally(() => setIsRefreshingMessages(false));
  }, []);

  /**
   * Loads the purchase order if one exists
   */
  const loadPurchaseOrder = useCallback((invId: number) => {
    setIsLoadingPurchaseOrder(true);
    apiGetPurchaseOrdersByInvoice(invId)
      .then(setPurchaseOrders)
      .catch((error) => {
        alert('Error loading purchase order. Please try again later');
        console.error(error);
      })
      .finally(() => setIsLoadingPurchaseOrder(false));
  }, []);

  useEffect(() => {
    loadPurchaseOrder(invoiceId);
  }, [invoiceId, loadPurchaseOrder]);

  /**
   * Sends a text message in the chat
   */
  const handleTextMessageSend = useCallback(
    (invId: number, text: string) => {
      apiSENDTEXTORDERMESSAGE(invId, text)
        .then((response) => {
          if (response.success) refreshMessages(invId);
          else throw new Error(response.message);
        })
        .catch((error) => {
          alert('Error sending message');
          console.error(error);
        });
    },
    [refreshMessages],
  );

  // Load messages on mount
  useEffect(() => {
    loadMessages(invoiceId);
  }, [invoiceId, loadMessages]);

  // Refresh messages if a new socket event arrives
  useEffect(() => {
    if (newInvoiceMessage?.invoice.id === invoiceId) {
      refreshMessages(invoiceId);
    }
  }, [newInvoiceMessage, invoiceId, refreshMessages]);

  /**
   * Loads all products if needed (stale or empty)
   */
  const loadAllProducts = useCallback(() => {
    const {user, productState} = props;
    const {lastUpdate, products} = productState;

    const productsAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(8, 'hours'),
    );
    const noProducts = !products?.length;

    if (productsAreExpired || noProducts) {
      setIsLoadingAllProducts(true);
      apiGetInputProducts(user)
        .then((fetchedProducts) => reduxSetProducts(fetchedProducts))
        .catch((error) => {
          alert('Error loading products. Please try again later');
          console.error(error);
        })
        .finally(() => setIsLoadingAllProducts(false));
    }
  }, [props]);

  useEffect(loadAllProducts, [loadAllProducts]);

  const handleOrderUpdates = (updatedOrders: IFarmInputOrder[]) => {
    const requests = updatedOrders.map((order) => {
      return apiSENDREPLACEMENTORDERMESSAGE({
        invoiceId,
        orderId: order.id,
        quantity: order.quantity,
        price: order.price,
        replacementProductStockCode: order.product.stockCode,
      });
    });

    Promise.all(requests)
      .then(() => refreshMessages(invoiceId))
      .catch((error) => {
        alert('Error updating order');
        console.error(error);
      });
  };

  /**
   * Downloads the invoice PDF, with a time-based cache buster
   */
  const handleDownload = () => {
    if (!invoice) return;
    const url = createCacheAvoidingUrl(
      invoice.documentUrl || invoice.proformaInvoiceUrl || '',
    );
    const newWindow = window.open(url, '_blank');
    if (newWindow) newWindow.opener = null;
  };

  const createCacheAvoidingUrl = (fileUrl: string) => {
    const searchParamToAvoidCache = `date=${new Date().getTime()}`;
    const appendChar = fileUrl.includes('?') ? '&' : '?';
    return `${fileUrl}${appendChar}${searchParamToAvoidCache}`;
  };

  /**
   * Copies invoice to a new quote
   */
  const handleCopyQuote = () => {
    NavigationService.navigate('/create-quote?invoice=' + invoiceId);
  };

  /**
   * Access only active products
   */
  const activeProducts = props.productState.products.filter(
    (product) => product.isActive,
  );

  /**
   * Supporting documents CRUD
   */
  const loadSupportingDocuments = useCallback(() => {
    return apiGetInvoiceSupportingDocuments(invoiceId);
  }, [invoiceId]);

  const updateSupportingDocument = useCallback(
    (document: ISupportingDocumentInput) => {
      return apiSaveInvoiceSupportingDocument({invoiceId, ...document}).then(
        () => undefined,
      );
    },
    [invoiceId],
  );

  const removeSupportingDocument = useCallback(
    (documentId: string | number) => {
      return apiDeleteInvoiceSupportingDocument(documentId).then(
        () => undefined,
      );
    },
    [],
  );

  /**
   * Permissions
   */
  const [permissions] = useState(() => new PermissionsService(user));
  const canWriteQuote = permissions.hasPermission(
    ESupplierUserPermissions.QUOTE_WRITE,
  );
  const canReadChat = permissions.hasPermission(
    ESupplierUserPermissions.ORDER_CHAT_READ,
  );
  const canWriteChat = permissions.hasPermission(
    ESupplierUserPermissions.ORDER_CHAT_WRITE,
  );
  const canWriteDocs = permissions.hasPermission(
    ESupplierUserPermissions.ORDER_MANAGE_DOCUMENTS,
  );
  // const canReadAndWritePurchaseOrders = user.isAECI;

  /**
   * Views an existing purchase order
   */
  const handleViewPurchaseOrder = () => {
    if (purchaseOrders.length === 1) {
      NavigationService.navigate('/purchase-orders/' + purchaseOrders[0].id);
    } else {
      NavigationService.navigate('/purchase-orders?invoiceId=' + invoiceId);
    }
  };

  return (
    <div className="invoice-detail">
      <AppBar className="app-bar" elevation={0}>
        <Toolbar>
          <Route
            render={({history}) => (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() =>
                  history.location.key
                    ? history.goBack()
                    : history.replace('/invoices')
                }
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            )}
          />
          <Typography variant="h6" className="title">
            Invoice {invoice?.invoiceNumber}{' '}
            {invoice?.proformaInvoiceUrl && !invoice?.documentUrl
              ? '(Proforma)'
              : ''}
          </Typography>

          {(invoice?.documentUrl || invoice?.proformaInvoiceUrl) && (
            <Tooltip title="Download Invoice" aria-label="download">
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleDownload}
                aria-label="close"
              >
                <GetAppIcon />
              </IconButton>
            </Tooltip>
          )}

          {/*
            Use MyHidden to avoid the TS error in MUI v4.
            Return null if canWriteQuote is false, to ensure we
            pass only valid React nodes (not 'false') as children.
          */}
          <MyHidden xsDown>
            {canWriteQuote ? (
              <Tooltip title="Copy To New Quote" aria-label="copy to new quote">
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={handleCopyQuote}
                  aria-label="copy to new quote"
                  style={{marginLeft: 4}}
                >
                  <LibraryAddIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </MyHidden>

          {/* canReadAndWritePurchaseOrders &&
            !isLoadingPurchaseOrder &&
            purchaseOrders.length === 0 && (
              <Tooltip
                title="Create Purchase Order"
                aria-label="create purchase order"
              >
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => setShowCreatePurchaseOrderConfirmation(true)}
                  aria-label="create purchase order"
                  style={{marginLeft: 4}}
                >
                  <NoteAddIcon />
                </IconButton>
              </Tooltip>
            )*/}
          {!isLoadingPurchaseOrder && purchaseOrders.length > 0 && (
            <Tooltip
              title="View Purchase Order"
              aria-label="view purchase order"
            >
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleViewPurchaseOrder}
                aria-label="view purchase order"
                style={{marginLeft: 4}}
              >
                <AssignmentIcon />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>

      <AppBar
        position="relative"
        elevation={0}
        variant="outlined"
        className="tabs-container"
        style={{display: showTabs ? '' : 'none'}}
      >
        <Tabs
          value={tabIndex}
          onChange={(event: React.ChangeEvent<{}>, newIndex: number) =>
            setTabIndex(newIndex)
          }
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value={0} label="Details" />
          <Tab value={1} label="Chat" />
        </Tabs>
      </AppBar>

      <Grid container className="container">
        <Grid
          item
          xs={12}
          md={8}
          className="grid-column invoice-detail-details-column"
          style={{display: showTabs && tabIndex === 1 ? 'none' : ''}}
        >
          <Paper className="order-info-container" variant="outlined">
            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Invoice Details</Typography>
              </Box>
              <InvoiceDetailCard
                isLoading={isLoadingInvoice}
                invoice={invoice}
                user={user}
              />
            </Box>
            <Box className="order-products-container-2">
              <Box className="header-container">
                <Typography variant="subtitle1">Purchase Orders</Typography>
              </Box>
              <InvoicePurchaseOrders
                invoiceId={invoiceId}
                isLoading={isLoadingPurchaseOrder}
                purchaseOrders={purchaseOrders}
                isTaxInvoice={!!invoice?.documentUrl}
                onCreatedTaxInvoice={() => loadInvoice(invoiceId)}
              />
            </Box>

            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Farm Details</Typography>
              </Box>
              <FarmDetailCard
                isLoading={isLoadingInvoice}
                farmer={invoice?.farmer}
              />
            </Box>

            <Box className="order-products-container-2">
              <Box className="header-container">
                <Typography variant="subtitle1">Line Items</Typography>
              </Box>
              <InvoiceProducts
                isLoading={isLoadingInvoice || isLoadingAllProducts}
                invoice={invoice}
                allInputProducts={activeProducts}
                onOrderUpdate={handleOrderUpdates}
                readonly={true}
              />
            </Box>

            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Logistics</Typography>
              </Box>
              <InvoiceLogisticsCard
                isLoading={isLoadingInvoice}
                invoice={invoice}
              />
            </Box>

            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">
                  Supporting Documents
                </Typography>
              </Box>
              <SupportingDocuments
                readonly={!canWriteDocs}
                loadSupportingDocuments={loadSupportingDocuments}
                updateSupportingDocument={updateSupportingDocument}
                removeSupportingDocument={removeSupportingDocument}
                titleOptions={[
                  EPurchaseOrderSupportingDocs.Waybill,
                  EPurchaseOrderSupportingDocs.DeliveryNote,
                  EPurchaseOrderSupportingDocs.Invoice,
                ]}
              />
            </Box>

            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Credit Note</Typography>
              </Box>
              <CreditNoteCard invoiceId={invoiceId} />
            </Box>
          </Paper>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
          className="grid-column invoice-detail-chat-column"
          style={{display: showTabs && tabIndex === 0 ? 'none' : ''}}
        >
          {canReadChat && (
            <InvoiceChat
              isLoading={isLoadingMessages}
              isRefreshing={isRefreshingMessages}
              myProfilePicture={user.pictureUrl}
              invoice={invoice}
              messages={messages}
              onMessageSend={(text) => handleTextMessageSend(invoiceId, text)}
              readonly={!canWriteChat}
            />
          )}
        </Grid>
      </Grid>

      <CreatePurchaseOrderFromOrderDialog
        invoiceId={invoiceId}
        isOpen={showCreatePurchaseOrderConfirmation}
        onClose={() => setShowCreatePurchaseOrderConfirmation(false)}
      />
    </div>
  );
};

const CreatePurchaseOrderFromOrderDialog = (props: {
  invoiceId: number;
  isOpen: boolean;
  onClose: VoidFunction;
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [expectedPaymentDate, setExpectedPaymentDate] = useState<Moment | null>(
    null,
  );

  const handleCreatePurchaseOrder = () => {
    setIsCreating(true);
    apiSavePurchaseOrder({
      invoiceId: props.invoiceId,
      expectedPaymentDate: expectedPaymentDate?.toDate(),
    })
      .then((purchaseOrder) => {
        reduxAddPurchaseOrder(purchaseOrder);
        return purchaseOrder;
      })
      .then((purchaseOrder) => {
        // Example: any additional logic here
        return purchaseOrder;
      })
      .then((purchaseOrder) => {
        setTimeout(() => {
          NavigationService.navigate('/purchase-orders/' + purchaseOrder?.id);
        }, 500);
      })
      .catch((error) => {
        alert('Error creating purchase order. Please try again later');
        console.error(error);
      })
      .finally(() => setIsCreating(false));
  };

  return (
    <Dialog
      className="quote-line-item-remove-confirmation-dialog positive-dialog"
      disableEscapeKeyDown
      open={props.isOpen}
      onClose={props.onClose}
    >
      <DialogTitle className="heading">Create Purchase Order</DialogTitle>
      <DialogContent className="body">
        Are you sure you want to create a purchase order?
      </DialogContent>
      <Box padding={2}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <KeyboardDatePicker
            autoOk
            variant="inline"
            inputVariant="outlined"
            label="Expected Payment Date (Optional)"
            format="DD/MM/yyyy"
            value={expectedPaymentDate}
            onChange={setExpectedPaymentDate}
            fullWidth
          />
        </MuiPickersUtilsProvider>
      </Box>
      <DialogActions>
        <Button className="cancel-button" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          className="accept-button"
          onClick={handleCreatePurchaseOrder}
          disabled={isCreating}
        >
          {isCreating ? (
            <CircularProgress color="primary" size={24} thickness={6} />
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
